import { useCallback } from 'react';
import { fetchSearchResults } from '../api/matchmakingApi';
import { extractProvidersFromResponse, processHistoryResults } from '../utils/search-results.utils';
import { PAGE_SIZE } from '../config/matchmaking.config';
import type { HistoryItem } from '../components/MatchmakingHistoryPopover';
type MatchmakingState = ReturnType<typeof import('./useMatchmakingState').useMatchmakingState>;

type UseHistorySearchProps = {
  advisorProfileId: string | null;
  selectedClientId: string | null;
  loadCount: number;
  matchmakingState: MatchmakingState;
  preloadedImages: Record<string, string | null>;
  setIsPreloadingImages: (value: boolean) => void;
  setPreloadedImages: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
  setShowNoProvidersFound: (value: boolean) => void;
  setNoProviderMessage: (message: string | null) => void;
  setError: (error: string | null) => void;
  setIsViewingHistoryResults: (value: boolean) => void;
  setCurrentSearchId: (id: number | null) => void;
  setLoadCount: (count: number) => void;
  setSearchCounter: (updater: (prev: number) => number) => void;
  lastPromptRef: React.MutableRefObject<string>;
  lastHistoryItemRef: React.MutableRefObject<HistoryItem | null>;
  setIsLoading: (value: boolean) => void;
  setIsLoadingMore: (value: boolean) => void;
};

export const useHistorySearch = ({
  advisorProfileId,
  selectedClientId,
  loadCount,
  matchmakingState,
  preloadedImages,
  setIsPreloadingImages,
  setPreloadedImages,
  setShowNoProvidersFound,
  setNoProviderMessage,
  setError,
  setIsViewingHistoryResults,
  setCurrentSearchId,
  setLoadCount,
  setSearchCounter,
  lastPromptRef,
  lastHistoryItemRef,
  setIsLoading,
  setIsLoadingMore,
}: UseHistorySearchProps) => {
  const fetchHistoryResults = useCallback(
    async (item: HistoryItem, isLoadMore = false) => {
      if (!advisorProfileId || !selectedClientId) return;

      const setLoadingState = isLoadMore ? setIsLoadingMore : setIsLoading;
      setLoadingState(true);
      setError(null);
      setShowNoProvidersFound(false);
      setNoProviderMessage(null);
      setIsViewingHistoryResults(true);
      lastPromptRef.current = item.promptText;

      const nextLoadCount = isLoadMore ? loadCount + 1 : 1;
      const totalRecordsToFetch =
        !isLoadMore && item.resultsCount ? item.resultsCount : PAGE_SIZE;
      const recordStart = (nextLoadCount - 1) * PAGE_SIZE + 1;
      const recordEnd = isLoadMore ? nextLoadCount * PAGE_SIZE : totalRecordsToFetch;

      try {
        const data = await fetchSearchResults({
          searchId: item.searchId,
          advisorId: advisorProfileId,
          clientId: selectedClientId,
          recordStart,
          recordEnd,
        });

        if (data.status && data.status.toUpperCase() === 'FAILED') {
          throw new Error(data.message || 'Failed to fetch search results');
        }

        const providers = extractProvidersFromResponse(data);

        if (providers.length === 0) {
          setIsPreloadingImages(false);
          setPreloadedImages({});
          matchmakingState.setClientMatches({
            matches: [],
            append: false,
            hasMore: false,
          });
          matchmakingState.setClientQuery(lastPromptRef.current);
          setShowNoProvidersFound(true);
          setNoProviderMessage('No saved results were found for this history item.');
          return;
        }

        const mappedMatches = await processHistoryResults(
          providers,
          preloadedImages,
          setIsPreloadingImages,
          setPreloadedImages,
        );

        const totalCount =
          typeof data.totalCount === 'number' ? data.totalCount : providers.length;

        matchmakingState.setClientMatches({
          matches: mappedMatches,
          append: isLoadMore,
          hasMore: totalCount > recordEnd,
        });

        matchmakingState.setClientQuery(item.promptText);
        setCurrentSearchId(item.searchId);
        const computedLoadCount = Math.max(nextLoadCount, Math.ceil(recordEnd / PAGE_SIZE));
        setLoadCount(computedLoadCount);
        setSearchCounter((prev) => prev + 1);
        lastHistoryItemRef.current = item;
      } catch (err) {
        setIsPreloadingImages(false);
        setPreloadedImages({} as Record<string, string | null>);
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch history results';
        console.error('Error fetching history results:', {
          error: err,
          searchId: item.searchId,
          advisorId: advisorProfileId,
          clientId: selectedClientId,
          recordStart,
          recordEnd,
        });
        setError(errorMessage);
      } finally {
        setLoadingState(false);
      }
    },
    [
      advisorProfileId,
      selectedClientId,
      loadCount,
      matchmakingState,
      preloadedImages,
      setIsPreloadingImages,
      setPreloadedImages,
      setShowNoProvidersFound,
      setNoProviderMessage,
      setError,
      setIsViewingHistoryResults,
      setCurrentSearchId,
      setLoadCount,
      setSearchCounter,
      lastPromptRef,
      lastHistoryItemRef,
      setIsLoading,
      setIsLoadingMore,
    ],
  );

  return { fetchHistoryResults };
};

